import { inject, Injectable, Injector } from '@angular/core';
import { customChunk, customKeyBy, uniq } from '@app/_v3/helpers/utils_map';
import { SrvDefaults, TServicesMap } from '@app/_v3/services/srvdefaults.config';
import { CachedService, AccountService, CacheTtlRule, MeetingsBffService } from '@core/services';
import { getStrategyPayload, map_images, map_to_legacy, STREAM_ROUTES } from '@core/services/bff/bff-v2.service';
import type { BffGetListMeetingsEndpoint } from 'lingo2-api-models';
import {
  IPagedResults,
  IContentPane,
  IPagination,
  User,
  AnyType,
  FindContentStrategyEnum,
  FindAccountStrategyEnum,
  IContentFilterBlock,
  EContentPaneFormat,
  EContentPanelRoute,
  FindMeetingsStrategyEnum,
  type IFindMeetingsFilter,
  type FindMeetingsFilter,
  type EDashboardMeetingGroup,
} from 'lingo2-models';
import { Observable, of, zip } from 'rxjs';
import { catchError, first, map, switchMap, takeUntil, tap } from 'rxjs/operators';

const DEFAULT_STRATEGY = 'owned';

/*
 */
export interface IDefContentPane extends IContentPane {
  contentItems?: any[];
}

/*
 */
@Injectable({ providedIn: 'root' })
export class PaneDataLoaderService {
  private readonly injector = inject(Injector);
  private readonly cachedService = inject(CachedService);
  private readonly accountService = inject(AccountService);
  private readonly meetingsBffService = inject(MeetingsBffService);

  private _TAG = `[*SRV] ${this.constructor.name}:`;

  public getDataFromService(
    pane: IDefContentPane,
    s: TServicesMap,
    filter: any,
    destroyed$: Observable<any>,
  ): Observable<IDefContentPane> {
    // если есть meetingFilter, то игнорируем универсальную логику загрузки данных из сервиса
    // и переходим к загрузке митингов
    if (pane.meetingFilter) return this.loadMeetings(pane);

    pane.loading = true;
    pane.infinity = s.infinity;

    if (!s.service) {
      console.error(`${this._TAG} getDataFromService: service is undefined for pane format:`, pane.format, s);
      pane.contentItems = [];
      pane.pagination = this.getPagination(pane);
      pane.loaded = true;
      pane.loading = false;
      return of(pane);
    }

    const service = this.injector.get(s.service);

    if (!service || typeof service[s.method] !== 'function') {
      console.error(`${this._TAG} getDataFromService: service or method is not available:`, {
        service: !!service,
        method: s.method,
        serviceHasMethod: service ? typeof service[s.method] : 'N/A',
        paneFormat: pane.format,
        serviceConfig: s,
      });
      pane.contentItems = [];
      pane.pagination = this.getPagination(pane);
      pane.loaded = true;
      pane.loading = false;
      return of(pane);
    }

    const args = s._details?.(this) ?? [];
    const reqFilter = s.payload_type === 'api_v1' ? filter : { ...filter, pagination: this.getPagination(pane) };

    let callArgs = [];
    if (s.payload_type === 'api_v1') {
      callArgs = [reqFilter, this.getPagination(pane), ...args];
    } else {
      if (reqFilter.filter?.order) reqFilter.order = reqFilter.filter?.order;
      callArgs = [reqFilter, ...args];
    }

    if (s.payload_type === 'api_bff') {
      pane.api_version = 'api_bff';
    } // Todo. Not idea for now why i made it
    console.debug(`${this._TAG} getDataFromService: `, s, reqFilter, pane.format, pane);
    return service[s.method](...callArgs).pipe(
      tap((res: IPagedResults<any[]>) => {
        console.debug(`####FROM BACK`, pane, res);
      }),
      map(map_to_legacy),
      map(map_images),
      switchMap((res: IPagedResults<any[]>) =>
        this.extendUsersPaginated$(res, 'author_id', 'author', 'FOREVER', destroyed$),
      ),
      tap((res: IPagedResults<IDefContentPane[]>) => {
        console.debug(`${this._TAG} getDataFromService->res: `, pane.format, reqFilter, res);

        const isPaginatedAppend = pane.infinity && (pane.pagination?.page ?? 1) > 1;
        const prev = Array.isArray(pane.contentItems) ? pane.contentItems : [];
        const next = Array.isArray(res.results) ? res.results : [];

        // Deduplication tricky way, scusa...
        const seen = new Set(prev.map((x) => x?.id || x?.slug));
        const merged = isPaginatedAppend ? [...prev, ...next.filter((x) => !seen.has(x?.id || x?.slug))] : next;

        pane.contentItems = merged;
        pane.pagination = res.pagination;
        pane.loaded = true;
        pane.loading = false;
      }),

      catchError((err) => {
        console.error(`${this._TAG} getDataFromService->err: `, pane, pane.format, err);
        pane.contentItems = [];
        pane.pagination = this.getPagination(pane);
        pane.loaded = true;
        pane.loading = false;
        return of(null);
      }),
    );
  }

  /*
   */
  public makeupMethod(pane: IDefContentPane, s: TServicesMap): string {
    const filter = pane?.filter || pane?.[s.filter];
    if (s === SrvDefaults.ServicesMap.content && filter?.related_to) {
      return 'getRelated';
    }
    return s.method;
  }

  /*
   */
  public buildPayload(pane: IDefContentPane, s: TServicesMap): any {
    const filter = pane?.filter || pane?.[s.filter];
    if (s.payload_type === 'api_v1') {
      /* TODO hardcoded content */
      return s === SrvDefaults.ServicesMap.content && filter?.related_to ? [filter.related_to, filter] : filter;
    }
    const strategy = FindContentStrategyEnum[filter?.use] || FindAccountStrategyEnum[filter?.use] || DEFAULT_STRATEGY;
    return getStrategyPayload(filter, strategy, {});
  }

  /*
   */
  public extendUsersPaginated$<T = AnyType>(
    pagedValues: IPagedResults<T[]>,
    keyUserId = 'user_id',
    keyUser = 'user',
    ttlSec: CacheTtlRule,
    destroyed$: Observable<any>,
  ): Observable<IPagedResults<T[]>> {
    const ids = this.pluckArray(pagedValues.results, keyUserId, keyUser);
    if (!ids.length) return of(pagedValues);

    const cachedUsers: { [id: string]: User } = {};
    ids.forEach((id) => {
      const user = this.cachedService.get<User>(['Account', id, undefined]);
      if (user) cachedUsers[id] = user;
    });

    const remainingIds = ids.filter((id) => !cachedUsers[id]);
    const cachedArray = Object.values(cachedUsers);
    const chunks = customChunk(remainingIds, 50);

    const requests = chunks.map((chunk) =>
      this.accountService
        .findAccounts({ id: chunk, use: FindAccountStrategyEnum.id }, { page: 1, pageSize: chunk.length })
        .pipe(map((res: IPagedResults<User[]>) => res.results)),
    );

    return requests.length
      ? zip(...requests).pipe(
          map((chunks) => chunks.flat()),
          tap((users) => {
            users.forEach((u) => this.cachedService.set(['Account', u.id, undefined], u, ttlSec));
          }),
          map((users) => {
            const userMap = customKeyBy([...users, ...cachedArray], 'id');
            pagedValues.results.forEach((item) => {
              if (item[keyUserId]) item[keyUser] = userMap[item[keyUserId]];
            });
            return pagedValues;
          }),
          takeUntil(destroyed$),
        )
      : of(pagedValues);
  }

  /*
   */
  private getPagination(pane: IDefContentPane): IPagination {
    // return pane.pagination || { page: 1, pageSize: 10, total: 0, totalPages: 0 };
    return {
      page: pane.pagination?.page ?? 1,
      pageSize: pane.pagination?.pageSize ?? 10,
      total: pane.pagination?.total ?? 0,
      totalPages: pane.pagination?.totalPages ?? 0,
    };
  }

  /*
   */
  private pluckArray(items: AnyType[], keyId: string, keyEntity: string): string[] {
    return uniq(
      items
        .filter((item) => !item[keyEntity])
        .map((item) => item[keyId])
        .filter(Boolean),
    );
  }

  /*
   */
  public loadPane(
    pane: IDefContentPane,
    destroyed$: Observable<any>,
    forceReload = false,
  ): Observable<IDefContentPane | null> {
    if (!pane || pane.loading || (!forceReload && pane.loaded)) return of(null);
    // сразу переходим к загрузке митингов, если есть meetingFilter
    if (pane.meetingFilter) return this.loadMeetings(pane);

    // Далее универсальная логика загрузки данных из сервиса
    const service = Object.values(SrvDefaults.ServicesMap).find((srv) => {
      if (!pane[srv.filter]) return null;
      if (!srv.strategy?.length) return srv;
      if (srv.strategy?.includes(pane[srv.filter]?.use)) {
        return srv;
      }
      return null;
    });

    if (!service && pane.format !== EContentPaneFormat.categories) {
      console.warn(`${this._TAG} Can't find Service for pane: `, pane, service);
    }

    const filter = pane?.filter || pane?.[service?.filter];
    console.debug(`${this._TAG} loadPane: `, service, filter, pane.format);

    if (service && filter) {
      if (!service.service) {
        console.error(`${this._TAG} Service is not defined in service map:`, service, pane.format);
        pane.contentItems = [];
        pane.pagination = this.getPagination(pane);
        pane.loaded = true;
        pane.loading = false;
        return of(pane);
      }

      if (pane.replace_content_type && !STREAM_ROUTES.includes(pane.baseRoute)) {
        return this.loadByReplaceType(pane, service, filter, destroyed$);
      }

      const method = this.makeupMethod(pane, service);
      const payload = this.buildPayload(pane, service);
      return this.getDataFromService(pane, { ...service, method }, payload, destroyed$);
    }

    // Special handlers
    if (pane.format === EContentPaneFormat.categories || pane.directoriesFilter) {
      return this.loadFilters(pane, destroyed$);
    }

    if (pane.meetingReviewsFiler) {
      return this.loadMeetingReviews(pane, destroyed$);
    }

    if (pane.userId) {
      return this.loadSingleUser(pane, destroyed$);
    }

    pane.loaded = true;
    pane.loading = false;
    return of(pane);
  }

  /*
   */
  private loadSingleUser(pane: IDefContentPane, destroyed$: Observable<any>): Observable<IDefContentPane> {
    pane.loading = true;
    return this.accountService.getUserById(pane.userId, 'FOREVER').pipe(
      first(),
      tap((user) => {
        pane.user = user;
        pane.loaded = true;
        pane.loading = false;
      }),
      map(() => pane),
      catchError((err) => {
        console.warn('[PaneDataLoader] loadSingleUser error', err);
        pane.loaded = true;
        pane.loading = false;
        return of(pane);
      }),
      takeUntil(destroyed$),
    );
  }

  /*
   */
  private loadFilters(pane: IDefContentPane, destroyed$: Observable<any>): Observable<IDefContentPane> {
    const section: 'subjects' | 'categories' = pane.format === EContentPaneFormat.subjects ? 'subjects' : 'categories';

    if (pane.contentItems?.length) {
      const filtered = (pane.contentItems as IContentFilterBlock[]).filter(
        (el) => el.code !== 'vocabulary' && el.code !== 'news',
      );
      pane.filters = filtered;
      pane.loaded = true;
      pane.loading = false;
      return of(pane);
    }

    const service = this.injector.get(SrvDefaults.ServicesMap.content.service);

    /*
     */
    return service.getContentFilterBlocks(section, pane.directoriesFilter).pipe(
      map((blocks: IContentFilterBlock[]) => blocks.filter((el) => el.code !== 'vocabulary' && el.code !== 'news')),
      tap((filtered) => {
        pane.filters = filtered;
        pane.loaded = true;
        pane.loading = false;
      }),
      catchError((err) => {
        console.warn('[PaneDataLoader] loadFilters error', err);
        pane.loaded = true;
        pane.loading = false;
        return of([]);
      }),
      map(() => pane),
      takeUntil(destroyed$),
    );
  }

  /*
   */
  private loadByReplaceType(
    pane: IDefContentPane,
    s: TServicesMap,
    filter: any,
    destroyed$: Observable<any>,
  ): Observable<IDefContentPane> {
    const type = pane.replace_content_type_type;
    const handler = {
      [EContentPanelRoute.lessons]: () =>
        this.getDataFromService(pane, { ...s, method: 'getContent' }, [filter], destroyed$),
      [EContentPanelRoute.classes]: () =>
        this.getDataFromService(pane, SrvDefaults.ServicesMap.userservices_bff, [filter], destroyed$),
      [EContentPanelRoute.schools]: () =>
        this.getDataFromService(pane, SrvDefaults.ServicesMap.schools, [filter], destroyed$),
      [EContentPanelRoute.collections]: () =>
        this.getDataFromService(pane, SrvDefaults.ServicesMap.collections, [filter], destroyed$),
      [EContentPanelRoute.tutors]: () =>
        this.getDataFromService(pane, SrvDefaults.ServicesMap.accounts, filter, destroyed$),
    }[type];

    if (!handler) {
      console.warn(`[PaneDataLoader] No replace handler for type: ${type}`);
      pane.loaded = true;
      pane.loading = false;
      return of(pane);
    }

    return handler();
  }

  public loadMeetingReviews(pane: IDefContentPane, destroyed$: Observable<any>, _f?: any): Observable<IDefContentPane> {
    pane.loaded = true;
    pane.loading = false;
    return of(pane);
  }

  public loadMeetings(pane: IDefContentPane) {
    let dto: BffGetListMeetingsEndpoint.Body = null;

    switch (pane.meetingFilter.use) {
      case FindMeetingsStrategyEnum.my_classes_events:
        dto = {
          filter: {},
          strategy: 'my-library',
          pagination: {
            page: pane.pagination?.page ?? 1,
            pageSize: pane.pagination?.pageSize ?? 10,
          },
        };
        break;
      default:
        console.warn(`[PaneDataLoader] No meetings handler for use: ${pane.meetingFilter.use}`);
        return of(pane);
    }

    if ('group' in pane.meetingFilter && pane.meetingFilter.group) {
      dto.filter.group = pane.meetingFilter.group as EDashboardMeetingGroup;
    }

    return this.meetingsBffService.getList(dto).pipe(
      map((response) => {
        pane.contentItems = response.list;
        pane.pagination = response.pagination;
        pane.loaded = true;
        pane.loading = false;

        return pane;
      }),
    );
  }
}
